import { API_ENDPOINTS } from '../config/api';

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type StandardGrantRow = {
  standardCode: string;
  enabled: boolean;
  source?: string | null;
  grantedAt?: string;
  expiresAt?: string | null;
};

export type EnrichedStandardAccessItem = {
  standardCode: string;
  displayName?: string;
  granted: boolean;
  grantSources: string[];
  expiresAt?: string | null;
  active: boolean;
  activationSources: string[];
  scopeSummary?: {
    organizationWide?: boolean;
    informationSystemsCount?: number;
    assetsCount?: number | null;
  };
  grantedByOrganization?: boolean;
  grantedByUser?: boolean;
};

export type EffectiveStandardAccess = {
  organizationId: string;
  userId: string;
  standardCodes: string[];
  standards?: EnrichedStandardAccessItem[];
  grantedStandards: Array<{
    standardCode: string;
    grantedByOrganization: boolean;
    grantedByUser: boolean;
    sources: string[];
  }>;
  organizationGrants: StandardGrantRow[];
  userGrants: StandardGrantRow[];
  activeStandardCodes?: string[];
  restricted?: { restrictedMode?: boolean; allowedStandardCodes?: string[] };
  legacyEnabledStandards?: string[];
};

export type RestrictedAccessPayload = {
  enabled: boolean;
  mode?: string;
  reason?: string | null;
  standardCodes?: string[];
};

async function parseJson<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      (body as { message?: string; error?: string })?.message ||
      (body as { error?: string })?.error ||
      res.statusText;
    throw new Error(msg || `Request failed (${res.status})`);
  }
  return (body as { data: T }).data ?? (body as T);
}

export async function fetchEffectiveStandardAccess(
  organizationId: string,
  userId?: string,
): Promise<EffectiveStandardAccess> {
  const url = new URL(API_ENDPOINTS.STANDARDS_EFFECTIVE_ACCESS(organizationId));
  if (userId) url.searchParams.set('user_id', userId);
  const res = await fetch(url.toString(), {
    headers: getAuthHeaders(),
  });
  return parseJson<EffectiveStandardAccess>(res);
}

export async function fetchOrganizationStandardGrants(
  organizationId: string,
): Promise<{ grants: StandardGrantRow[]; catalog: Array<{ code: string; name?: string }> }> {
  const res = await fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_GRANTS(organizationId), {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function updateOrganizationStandardGrants(
  organizationId: string,
  standards: Array<{ standardCode: string; enabled?: boolean; source?: string }>,
): Promise<{ grants: StandardGrantRow[] }> {
  const res = await fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_GRANTS(organizationId), {
    method: 'PUT',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ standards }),
  });
  return parseJson(res);
}

export async function fetchUserStandardGrants(
  userId: string,
  organizationId: string,
): Promise<{
  organizationGrants: StandardGrantRow[];
  userGrants: StandardGrantRow[];
}> {
  const res = await fetch(
    API_ENDPOINTS.ADMIN_USER_STANDARD_GRANTS(userId, organizationId),
    { headers: getAuthHeaders() },
  );
  return parseJson(res);
}

export async function updateUserStandardGrants(
  userId: string,
  organizationId: string,
  standards: Array<{
    standardCode: string;
    enabled?: boolean;
    grantOrgToo?: boolean;
    source?: string;
  }>,
): Promise<{ organizationGrants: StandardGrantRow[]; userGrants: StandardGrantRow[] }> {
  const res = await fetch(
    API_ENDPOINTS.ADMIN_USER_STANDARD_GRANTS(userId, organizationId),
    {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ standards }),
    },
  );
  return parseJson(res);
}

export async function fetchRestrictedAccess(
  userId: string,
  organizationId: string,
): Promise<RestrictedAccessPayload & { standardCodes?: string[] }> {
  const res = await fetch(
    API_ENDPOINTS.ADMIN_USER_RESTRICTED_ACCESS(userId, organizationId),
    { headers: getAuthHeaders() },
  );
  return parseJson(res);
}

export async function updateRestrictedAccess(
  userId: string,
  organizationId: string,
  payload: RestrictedAccessPayload,
): Promise<RestrictedAccessPayload> {
  const res = await fetch(
    API_ENDPOINTS.ADMIN_USER_RESTRICTED_ACCESS(userId, organizationId),
    {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  return parseJson(res);
}

export async function deleteRestrictedAccess(
  userId: string,
  organizationId: string,
): Promise<void> {
  const res = await fetch(
    API_ENDPOINTS.ADMIN_USER_RESTRICTED_ACCESS(userId, organizationId),
    { method: 'DELETE', headers: getAuthHeaders() },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      (body as { message?: string })?.message || res.statusText || 'Delete failed',
    );
  }
}

export async function fetchStandardActivations(organizationId: string) {
  const res = await fetch(API_ENDPOINTS.STANDARDS_ACTIVATIONS(organizationId), {
    headers: getAuthHeaders(),
  });
  return parseJson<unknown[]>(res);
}

export async function previewGrantBackfill(organizationId: string) {
  const res = await fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_GRANTS_BACKFILL_PREVIEW(organizationId), {
    headers: getAuthHeaders(),
  });
  return parseJson<{
    wouldCreate: string[];
    alreadyGranted: string[];
    sources: Record<string, string[]>;
  }>(res);
}

export async function backfillOrganizationGrants(organizationId: string) {
  const res = await fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_GRANTS_BACKFILL(organizationId), {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson<{ created: number; existing: number; standards: string[] }>(res);
}

export async function previewActivationBackfill(organizationId: string) {
  const res = await fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_ACTIVATIONS_BACKFILL_PREVIEW(organizationId), {
    headers: getAuthHeaders(),
  });
  return parseJson<{ wouldCreate: unknown[]; existingCount: number }>(res);
}

export async function backfillOrganizationActivations(organizationId: string) {
  const res = await fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_ACTIVATIONS_BACKFILL(organizationId), {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson<{ created: number; existing: number }>(res);
}
